import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calculator,
  Plus,
  Trash2,
  Save,
  Wallet,
  Home,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { settingsApi } from "@/lib/crmApi";

/**
 * Admin panel — gère le catalogue des appartements ET les modes de paiement
 * (avec taux + échéancier) qui alimentent /simulation.
 *
 * Scope:
 *   - `projetId=null` (défaut) → édite la config GLOBALE
 *     (`app_settings.simulation_apartments` / `simulation_modes`)
 *   - `projetId="<uuid>"`      → édite l'OVERRIDE du projet
 *     (`project_settings[projet_id].simulation_apartments` / `simulation_modes`)
 *     Le GET retourne la vue *effective* (overrides mergés sur le global)
 *     pour que l'admin voie ce que les leads de ce projet voient vraiment.
 *
 * Props
 *   - projetId: string|null      // null = global
 *   - projetNom: string|null     // display label dans l'en-tête
 */

const blankApart = () => ({
  id: `apt-${Math.random().toString(36).slice(2, 8)}`,
  typo: "F2",
  unite: "",
  surface: 0,
  prix: 0,
  programme: "",
  // Modes that should NOT be offered for this apartment (e.g. ["location_vente"]
  // when SOGI sells F2 strictly cash).
  disabled_modes: [],
});

const blankStep = () => ({ etape: "", offset_months: 0, pct: 0 });

const blankMode = () => ({
  key: `mode-${Math.random().toString(36).slice(2, 6)}`,
  label: "Nouveau mode",
  enabled: true,
  kind: "schedule",        // "schedule" | "monthly"
  rate_pct: 0,
  schedule: [],
  durations_years: [],
  downpayment_pct: 0,
});

const fmtFCFA = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(n || 0)
  ) + " FCFA";

export default function SimulationConfigPanel({
  projetId = null,
  projetNom = null,
} = {}) {
  const [apartments, setApartments] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openMode, setOpenMode] = useState(null);
  // True when this project has its OWN simulation overrides — drives the
  // "héritée" / "override" badge and enables the "Reset" button.
  const [hasOverride, setHasOverride] = useState(false);

  // Refetch whenever the scope changes so switching projects in the parent
  // immediately reloads the right config without a remount.
  useEffect(() => {
    setLoading(true);
    const fetcher = projetId
      ? settingsApi.getForProject(projetId).then((r) => {
          // r = { projet_id, projet_nom, effective, overrides }
          const eff = r?.effective || {};
          const ov = r?.overrides || {};
          setHasOverride(
            Array.isArray(ov.simulation_apartments) ||
            Array.isArray(ov.simulation_modes)
          );
          return eff;
        })
      : settingsApi.get().then((s) => {
          setHasOverride(false);
          return s;
        });
    fetcher
      .then((s) => {
        setApartments(Array.isArray(s.simulation_apartments) ? s.simulation_apartments : []);
        setModes(Array.isArray(s.simulation_modes) ? s.simulation_modes : []);
      })
      .catch(() => toast.error("Impossible de charger la configuration"))
      .finally(() => setLoading(false));
  }, [projetId]);

  // --- Apartments ---------------------------------------------------------
  const addApart = () => setApartments((a) => [...a, blankApart()]);
  const delApart = (id) => setApartments((a) => a.filter((x) => x.id !== id));
  const setApart = (id, field, value) =>
    setApartments((a) => a.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  // --- Modes --------------------------------------------------------------
  const addMode = () => setModes((m) => [...m, blankMode()]);
  const delMode = (key) =>
    setModes((m) => m.filter((x) => x.key !== key));
  const setModeField = (key, field, value) =>
    setModes((m) => m.map((x) => (x.key === key ? { ...x, [field]: value } : x)));
  const addStep = (key) =>
    setModes((m) =>
      m.map((x) =>
        x.key === key
          ? { ...x, schedule: [...(x.schedule || []), blankStep()] }
          : x
      )
    );
  const delStep = (key, idx) =>
    setModes((m) =>
      m.map((x) =>
        x.key === key
          ? { ...x, schedule: (x.schedule || []).filter((_, i) => i !== idx) }
          : x
      )
    );
  const setStep = (key, idx, field, value) =>
    setModes((m) =>
      m.map((x) =>
        x.key === key
          ? {
              ...x,
              schedule: (x.schedule || []).map((s, i) =>
                i === idx ? { ...s, [field]: value } : s
              ),
            }
          : x
      )
    );

  // --- Save ---------------------------------------------------------------
  const save = async () => {
    // Sanitize numbers + drop incomplete apartments / steps
    const cleanApts = apartments
      .filter((a) => a.unite?.trim())
      .map((a) => ({
        id: a.id,
        typo: a.typo || "",
        unite: a.unite || "",
        surface: Number(a.surface) || 0,
        prix: Number(a.prix) || 0,
        programme: a.programme || "",
        disabled_modes: Array.isArray(a.disabled_modes) ? a.disabled_modes : [],
      }));
    const cleanModes = modes
      .filter((m) => m.label?.trim())
      .map((m) => ({
        key: m.key,
        label: m.label,
        enabled: m.enabled !== false,
        kind: m.kind || "schedule",
        rate_pct: Number(m.rate_pct) || 0,
        schedule: (m.schedule || [])
          .filter((s) => s.etape?.trim())
          .map((s) => ({
            etape: s.etape,
            offset_months: Number(s.offset_months) || 0,
            pct: Number(s.pct) || 0,
          })),
        durations_years: Array.isArray(m.durations_years)
          ? m.durations_years.map((n) => Number(n) || 0).filter((n) => n > 0)
          : [],
        downpayment_pct: Number(m.downpayment_pct) || 0,
      }));

    // Hard rule: monthly modes must have apport >= 30% (politique SOGI).
    const badApport = cleanModes.filter(
      (m) => m.enabled && m.kind === "monthly" && (Number(m.downpayment_pct) || 0) < 30
    );
    if (badApport.length > 0) {
      toast.error(
        `Apport minimum 30% requis pour : ${badApport.map((m) => m.label).join(", ")}`
      );
      return;
    }
    // Friendly warning if a SCHEDULE mode's percentages don't sum to 100.
    // Monthly modes generate their own schedule from durations + apport so
    // their `schedule` array is intentionally empty.
    const badModes = cleanModes
      .filter((m) => m.enabled && m.kind !== "monthly" && m.schedule.length > 0)
      .map((m) => ({ label: m.label, total: m.schedule.reduce((s, x) => s + x.pct, 0) }))
      .filter((m) => Math.round(m.total) !== 100);
    if (badModes.length > 0) {
      const msg = badModes.map((b) => `${b.label}: ${b.total}%`).join(" · ");
      if (!window.confirm(`Attention : la somme des % ne fait pas 100 (${msg}). Enregistrer quand même ?`)) {
        return;
      }
    }

    setSaving(true);
    try {
      const body = {
        simulation_apartments: cleanApts,
        simulation_modes: cleanModes,
      };
      if (projetId) {
        await settingsApi.updateForProject(projetId, body);
      } else {
        await settingsApi.update(body);
      }
      setApartments(cleanApts);
      setModes(cleanModes);
      setHasOverride(!!projetId);
      toast.success(
        projetId
          ? `Configuration enregistrée pour « ${projetNom || "ce projet"} »`
          : "Configuration globale enregistrée"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Reset = wipe per-project overrides for these two fields. The project
  // immediately falls back to the global catalogue/modes.
  const resetToGlobal = async () => {
    if (!projetId) return;
    if (!window.confirm(
      `Réinitialiser la simulation de « ${projetNom || "ce projet"} » ?\n\n` +
      `Les appartements et modes spécifiques au projet seront supprimés. ` +
      `Le projet héritera automatiquement de la config globale.`
    )) return;
    setSaving(true);
    try {
      // We don't fully wipe project_settings (other fields like welcome_template
      // must survive) — we just blank the simulation_* arrays so the merge
      // falls back to global defaults.
      await settingsApi.updateForProject(projetId, {
        simulation_apartments: [],
        simulation_modes: [],
      });
      // Refetch to show the inherited global config
      const r = await settingsApi.getForProject(projetId);
      const eff = r?.effective || {};
      setApartments(Array.isArray(eff.simulation_apartments) ? eff.simulation_apartments : []);
      setModes(Array.isArray(eff.simulation_modes) ? eff.simulation_modes : []);
      setHasOverride(false);
      toast.success("Réinitialisé — hérite désormais du global");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-border/60 bg-card p-5 text-sm text-muted-foreground">
        Chargement de la configuration…
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
      data-testid="section-simulation-config"
    >
      <header className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
          <Calculator className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-lg font-bold">
              Simulation — Catalogue & Modes
            </h2>
            {projetId ? (
              <span className={
                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                (hasOverride
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "bg-muted text-muted-foreground")
              }>
                {hasOverride ? `override · ${projetNom || "projet"}` : `hérite du global · ${projetNom || ""}`}
              </span>
            ) : (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                global · défauts
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {projetId
              ? `Cette configuration ne s'applique QU'aux leads de « ${projetNom || "ce projet"} ». Les autres projets gardent les défauts globaux.`
              : "Configure les appartements, leurs prix et les modes de paiement (Comptant, Location-Vente…). Ces réglages sont les DÉFAUTS — chaque projet peut les surcharger individuellement."}
          </p>
        </div>
        {projetId && hasOverride && (
          <Button
            variant="outline"
            size="sm"
            onClick={resetToGlobal}
            disabled={saving}
            className="gap-1.5"
            data-testid="simu-config-reset"
          >
            Réinitialiser depuis global
          </Button>
        )}
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="simu-config-save">
          <Save className="h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </header>

      {/* Apartments table */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Appartements à vendre
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={addApart}
            className="gap-1.5"
            data-testid="simu-config-add-apt"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter
          </Button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-border/60">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-2 py-2 text-left">Typo</th>
                <th className="px-2 py-2 text-left">Unité</th>
                <th className="px-2 py-2 text-right">Surface</th>
                <th className="px-2 py-2 text-right">Prix (FCFA)</th>
                <th className="px-2 py-2 text-left">Programme</th>
                <th className="px-2 py-2 text-left">Modes disponibles</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {apartments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-4 text-center text-muted-foreground">
                    Aucun appartement. Clique « Ajouter » pour commencer.
                  </td>
                </tr>
              ) : (
                apartments.map((a) => (
                  <tr key={a.id} className="border-t border-border/40">
                    <td className="px-1 py-1">
                      <Input
                        value={a.typo}
                        onChange={(e) => setApart(a.id, "typo", e.target.value)}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={a.unite}
                        onChange={(e) => setApart(a.id, "unite", e.target.value)}
                        className="h-8 text-sm"
                        placeholder="F2-A"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="number"
                        value={a.surface}
                        onChange={(e) => setApart(a.id, "surface", Number(e.target.value) || 0)}
                        className="h-8 text-right text-sm tabular-nums"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="number"
                        value={a.prix}
                        onChange={(e) => setApart(a.id, "prix", Number(e.target.value) || 0)}
                        className="h-8 text-right text-sm tabular-nums"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={a.programme}
                        onChange={(e) => setApart(a.id, "programme", e.target.value)}
                        className="h-8 text-sm"
                        placeholder="RYAD Almadies"
                      />
                    </td>
                    <td className="px-1 py-1">
                      {/* Toggle each mode for this apartment. Defaults to ON;
                          unchecking = mode added to disabled_modes list. */}
                      <div className="flex flex-wrap gap-1">
                        {modes.map((m) => {
                          const disabledForThis =
                            (a.disabled_modes || []).includes(m.key);
                          return (
                            <button
                              key={m.key}
                              type="button"
                              onClick={() => {
                                const cur = new Set(a.disabled_modes || []);
                                if (cur.has(m.key)) cur.delete(m.key);
                                else cur.add(m.key);
                                setApart(a.id, "disabled_modes", [...cur]);
                              }}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition ${
                                disabledForThis
                                  ? "border-red-300 bg-red-50 text-red-700 line-through dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
                                  : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300"
                              }`}
                              title={
                                disabledForThis
                                  ? `${m.label} non proposé pour ${a.unite}`
                                  : `${m.label} proposé pour ${a.unite}`
                              }
                              data-testid={`apt-${a.id}-mode-${m.key}`}
                            >
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-1 py-1 text-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => delApart(a.id)}
                        className="h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          Clique sur un mode pour le retirer de cet appartement (apparaît barré
          en rouge). Les leads ne pourront pas le sélectionner sur la simulation.
        </p>
      </div>

      {/* Modes / Schedules */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Modes de paiement
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={addMode}
            className="gap-1.5"
            data-testid="simu-config-add-mode"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter un mode
          </Button>
        </div>

        <div className="space-y-2">
          {modes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-sm text-muted-foreground">
              Aucun mode défini.
            </div>
          ) : (
            modes.map((m) => {
              const total = (m.schedule || []).reduce(
                (s, x) => s + Number(x.pct || 0),
                0
              );
              const expanded = openMode === m.key;
              const Icon = m.key === "comptant" ? Wallet : Home;
              return (
                <div
                  key={m.key}
                  className="rounded-xl border border-border/60 bg-muted/10"
                >
                  <button
                    type="button"
                    onClick={() => setOpenMode(expanded ? null : m.key)}
                    className="flex w-full items-center justify-between gap-3 p-3 text-left"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Icon className="h-4 w-4 text-emerald-600" />
                      <span className="font-heading font-bold">{m.label}</span>
                      {!m.enabled && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">
                          Désactivé
                        </span>
                      )}
                      <span className="text-[11px] text-muted-foreground">
                        · {m.schedule?.length || 0} étape(s) · taux {m.rate_pct || 0}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          Math.round(total) === 100
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                        }`}
                      >
                        Σ {total}%
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 transition ${expanded ? "rotate-180" : ""}`}
                      />
                    </div>
                  </button>

                  {expanded && (
                    <div className="space-y-3 border-t border-border/40 p-3">
                      {/* Mode metadata */}
                      <div className="grid gap-2 sm:grid-cols-4">
                        <div className="space-y-1.5">
                          <Label className="text-[11px]">Libellé</Label>
                          <Input
                            value={m.label}
                            onChange={(e) => setModeField(m.key, "label", e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[11px]">Type</Label>
                          <select
                            value={m.kind || "schedule"}
                            onChange={(e) => setModeField(m.key, "kind", e.target.value)}
                            className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                          >
                            <option value="schedule">Échéancier fixe</option>
                            <option value="monthly">Mensuel (Location-Vente)</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[11px]">Taux annuel (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={m.rate_pct || 0}
                            onChange={(e) =>
                              setModeField(m.key, "rate_pct", Number(e.target.value) || 0)
                            }
                            className="h-8 text-sm tabular-nums"
                          />
                        </div>
                        <div className="flex items-center justify-between gap-2 sm:justify-start">
                          <Label className="text-[11px]">Activé</Label>
                          <Switch
                            checked={m.enabled !== false}
                            onCheckedChange={(v) => setModeField(m.key, "enabled", v)}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => delMode(m.key)}
                            className="ml-auto h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                            title="Supprimer ce mode"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Monthly-only fields: durations + apport */}
                      {m.kind === "monthly" && (
                        <div className="grid gap-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label className="text-[11px]">
                              Durées disponibles (années, séparées par des virgules)
                            </Label>
                            <Input
                              value={(m.durations_years || []).join(", ")}
                              onChange={(e) => {
                                const arr = e.target.value
                                  .split(/[ ,;]+/)
                                  .map((s) => Number(s.trim()))
                                  .filter((n) => n > 0 && n <= 30);
                                setModeField(m.key, "durations_years", arr);
                              }}
                              placeholder="3, 5, 8"
                              className="h-8 text-sm tabular-nums"
                              data-testid={`mode-${m.key}-durations`}
                            />
                            <p className="text-[10px] text-muted-foreground">
                              Le client choisira parmi ces durées sur la simulation.
                            </p>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[11px]">
                              Apport / Réservation (% du prix){" "}
                              <span className="text-amber-600 dark:text-amber-400">
                                — min 30%
                              </span>
                            </Label>
                            <Input
                              type="number"
                              step="0.5"
                              min={30}
                              value={m.downpayment_pct || 0}
                              onChange={(e) =>
                                setModeField(
                                  m.key,
                                  "downpayment_pct",
                                  Number(e.target.value) || 0
                                )
                              }
                              className={`h-8 text-sm tabular-nums ${
                                Number(m.downpayment_pct || 0) < 30
                                  ? "border-red-500 ring-1 ring-red-500"
                                  : ""
                              }`}
                              placeholder="30"
                            />
                            <p className="text-[10px] text-muted-foreground">
                              Versé au début. Politique SOGI : minimum 30%.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Schedule rows — only for "schedule" kind. Monthly
                          modes generate their schedule from the duration +
                          downpayment + rate, so the manual table is hidden. */}
                      {m.kind !== "monthly" && (
                      <div className="rounded-lg border border-border/60 bg-card">
                        <div className="grid grid-cols-[1.4fr_0.6fr_0.6fr_2rem] gap-2 border-b border-border/40 bg-muted/40 px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <span>Étape</span>
                          <span className="text-right">Mois (offset)</span>
                          <span className="text-right">%</span>
                          <span />
                        </div>
                        {(m.schedule || []).map((step, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-[1.4fr_0.6fr_0.6fr_2rem] items-center gap-2 border-b border-border/40 px-2 py-1 last:border-b-0"
                          >
                            <Input
                              value={step.etape}
                              onChange={(e) => setStep(m.key, idx, "etape", e.target.value)}
                              className="h-7 text-sm"
                              placeholder="Réservation"
                            />
                            <Input
                              type="number"
                              value={step.offset_months}
                              onChange={(e) =>
                                setStep(m.key, idx, "offset_months", Number(e.target.value) || 0)
                              }
                              className="h-7 text-right text-sm tabular-nums"
                            />
                            <Input
                              type="number"
                              step="0.1"
                              value={step.pct}
                              onChange={(e) =>
                                setStep(m.key, idx, "pct", Number(e.target.value) || 0)
                              }
                              className="h-7 text-right text-sm tabular-nums"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => delStep(m.key, idx)}
                              className="h-6 w-6 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addStep(m.key)}
                          className="flex w-full items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                        >
                          <Plus className="h-3 w-3" />
                          Ajouter une étape
                        </button>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
